import type { WorkBook, WorkSheet } from "xlsx";

import type { AvailableSlotOption } from "./AvailableTimeSlotsField";

const REQUIREMENT_SHEET = "班级课程需求";
const ALLOCATION_SHEET = "教师任课分配";
const WEEKDAYS = ["周一", "周二", "周三", "周四", "周五"] as const;
const REQUIREMENT_HEADERS = [
  "班级",
  "课程",
  "周课时",
  "上课周次",
  "连课",
  "同日可多节",
  "功能教室",
  "可排方式"
] as const;
const ALLOCATION_HEADERS = [
  "班级",
  "课程",
  "主教老师",
  "助教老师1",
  "助教老师2",
  "助教老师3",
  "最少课时",
  "最多课时"
] as const;

interface NamedItem {
  id: string;
  name: string;
}

interface ClassItem {
  id: string;
  grade_label: string;
  class_name: string;
}

export interface PlanningWorkbookCatalog {
  classes: ClassItem[];
  courses: NamedItem[];
  teachers: NamedItem[];
  rooms: NamedItem[];
  slots: AvailableSlotOption[];
}

export interface ParsedPlanningWorkbook {
  requirements: Array<{
    source_row: number;
    class_id: string;
    course_id: string;
    weekly_lessons: number;
    week_pattern: "EVERY" | "ODD" | "EVEN";
    consecutive: boolean;
    allow_multiple_groups_per_day: boolean;
    room_type_id: string | null;
    time_preference: "ANY";
    available_time_slot_ids: string[] | null;
    available_time_cells: Array<{ weekday: number; time_slot_id: string }> | null;
  }>;
  allocations: Array<{
    source_row: number;
    class_id: string;
    course_id: string;
    main_teacher_id: string;
    assistant_teacher_ids: string[];
    weekly_lessons_min: number;
    weekly_lessons_max: number;
  }>;
}

export class PlanningWorkbookError extends Error {
  readonly sheetName?: string;
  readonly sourceRow?: number;
  readonly field?: string;

  constructor(
    message: string,
    location: { sheetName?: string; sourceRow?: number; field?: string } = {}
  ) {
    super(message);
    this.name = "PlanningWorkbookError";
    this.sheetName = location.sheetName;
    this.sourceRow = location.sourceRow;
    this.field = location.field;
  }
}

export async function parsePlanningWorkbook(
  file: File,
  catalog: PlanningWorkbookCatalog
): Promise<ParsedPlanningWorkbook> {
  if (!file.name.toLocaleLowerCase().endsWith(".xlsx")) {
    throw new PlanningWorkbookError("仅支持 .xlsx 文件，请使用当前项目下载的模板");
  }
  let workbook: WorkBook;
  try {
    const XLSX = await import("xlsx");
    workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  } catch {
    throw new PlanningWorkbookError("无法读取该 Excel 文件，请确认文件没有损坏");
  }
  for (const sheetName of [REQUIREMENT_SHEET, ALLOCATION_SHEET]) {
    if (!workbook.Sheets[sheetName]) {
      throw new PlanningWorkbookError(`缺少工作表“${sheetName}”`, { sheetName });
    }
  }

  const sortedSlots = [...catalog.slots].sort(
    (left, right) =>
      left.condition_sort_order - right.condition_sort_order ||
      left.id.localeCompare(right.id)
  );
  const slotHeaders = planningSlotHeaders(sortedSlots);
  const classIds = uniqueLookup(
    catalog.classes.map((item) => [`${item.grade_label}${item.class_name}`, item.id]),
    "班级"
  );
  const courseIds = uniqueLookup(catalog.courses.map((item) => [item.name, item.id]), "课程");
  const teacherIds = uniqueLookup(catalog.teachers.map((item) => [item.name, item.id]), "教师");
  const roomIds = uniqueLookup(catalog.rooms.map((item) => [item.name, item.id]), "功能教室");

  const requirementRows = await sheetRows(
    workbook.Sheets[REQUIREMENT_SHEET]!,
    REQUIREMENT_SHEET,
    [...REQUIREMENT_HEADERS, ...slotHeaders],
    [[...REQUIREMENT_HEADERS, ...legacyPlanningSlotHeaders(slotHeaders)]]
  );
  const allocationRows = await sheetRows(
    workbook.Sheets[ALLOCATION_SHEET]!,
    ALLOCATION_SHEET,
    [...ALLOCATION_HEADERS]
  );
  const requirements = requirementRows.map(({ row, sourceRow }) => {
    const classId = lookupValue(classIds, row[0], REQUIREMENT_SHEET, sourceRow, "班级");
    const courseId = lookupValue(courseIds, row[1], REQUIREMENT_SHEET, sourceRow, "课程");
    const weeklyLessons = integerValue(row[2], 1, 15, REQUIREMENT_SHEET, sourceRow, "周课时");
    const weekPattern = enumValue(
      row[3],
      { "每周": "EVERY", "单周": "ODD", "双周": "EVEN" } as const,
      REQUIREMENT_SHEET,
      sourceRow,
      "上课周次"
    );
    const consecutive = booleanValue(row[4], REQUIREMENT_SHEET, sourceRow, "连课");
    const allowMultiple = booleanValue(
      row[5],
      REQUIREMENT_SHEET,
      sourceRow,
      "同日可多节"
    );
    const roomText = cellText(row[6]);
    const roomTypeId = roomText && roomText !== "无"
      ? lookupValue(roomIds, roomText, REQUIREMENT_SHEET, sourceRow, "功能教室")
      : null;
    const availabilityMode = enumValue(
      row[7],
      { "不限": "ANY", "指定节次": "CUSTOM" } as const,
      REQUIREMENT_SHEET,
      sourceRow,
      "可排方式"
    );
    const cells = WEEKDAYS.flatMap((_, weekdayIndex) =>
      sortedSlots.flatMap((slot, slotIndex) => {
        const column = 8 + weekdayIndex * sortedSlots.length + slotIndex;
        const value = cellText(row[column]);
        if (value && value !== "可排") {
          throw new PlanningWorkbookError("请选择“可排”或留空", {
            sheetName: REQUIREMENT_SHEET,
            sourceRow,
            field: slotHeaders[weekdayIndex * sortedSlots.length + slotIndex]
          });
        }
        return value === "可排"
          ? [{ weekday: weekdayIndex + 1, time_slot_id: slot.id }]
          : [];
      })
    );
    if (availabilityMode === "CUSTOM" && cells.length === 0) {
      throw new PlanningWorkbookError("选择“指定节次”后至少要选一个可排单元格", {
        sheetName: REQUIREMENT_SHEET,
        sourceRow,
        field: "可排方式"
      });
    }
    if (availabilityMode === "ANY" && cells.length > 0) {
      throw new PlanningWorkbookError("可排方式为“不限”时，右侧课表单元格应留空", {
        sheetName: REQUIREMENT_SHEET,
        sourceRow,
        field: "可排方式"
      });
    }
    return {
      source_row: sourceRow,
      class_id: classId,
      course_id: courseId,
      weekly_lessons: weeklyLessons,
      week_pattern: weekPattern,
      consecutive,
      allow_multiple_groups_per_day: allowMultiple,
      room_type_id: roomTypeId,
      time_preference: "ANY" as const,
      available_time_slot_ids:
        availabilityMode === "CUSTOM" ? [...new Set(cells.map((cell) => cell.time_slot_id))] : null,
      available_time_cells: availabilityMode === "CUSTOM" ? cells : null
    };
  });
  const allocations = allocationRows.map(({ row, sourceRow }) => {
    const classId = lookupValue(classIds, row[0], ALLOCATION_SHEET, sourceRow, "班级");
    const courseId = lookupValue(courseIds, row[1], ALLOCATION_SHEET, sourceRow, "课程");
    const mainTeacherId = lookupValue(
      teacherIds,
      row[2],
      ALLOCATION_SHEET,
      sourceRow,
      "主教老师"
    );
    const assistantTeacherIds = row.slice(3, 6).flatMap((value, index) => {
      const text = cellText(value);
      return !text || text === "无"
        ? []
        : [lookupValue(
            teacherIds,
            text,
            ALLOCATION_SHEET,
            sourceRow,
            `助教老师${index + 1}`
          )];
    });
    if (new Set(assistantTeacherIds).size !== assistantTeacherIds.length) {
      throw new PlanningWorkbookError("助教老师不能重复", {
        sheetName: ALLOCATION_SHEET,
        sourceRow,
        field: "助教老师"
      });
    }
    if (assistantTeacherIds.includes(mainTeacherId)) {
      throw new PlanningWorkbookError("主教老师不能同时作为助教", {
        sheetName: ALLOCATION_SHEET,
        sourceRow,
        field: "助教老师"
      });
    }
    const minimum = integerValue(row[6], 1, 15, ALLOCATION_SHEET, sourceRow, "最少课时");
    const maximum = integerValue(row[7], 1, 15, ALLOCATION_SHEET, sourceRow, "最多课时");
    if (minimum > maximum) {
      throw new PlanningWorkbookError("最少课时不能大于最多课时", {
        sheetName: ALLOCATION_SHEET,
        sourceRow,
        field: "课时范围"
      });
    }
    return {
      source_row: sourceRow,
      class_id: classId,
      course_id: courseId,
      main_teacher_id: mainTeacherId,
      assistant_teacher_ids: assistantTeacherIds,
      weekly_lessons_min: minimum,
      weekly_lessons_max: maximum
    };
  });
  if (requirements.length === 0 && allocations.length === 0) {
    throw new PlanningWorkbookError("两个业务工作表中都没有可导入的数据");
  }
  return { requirements, allocations };
}

export function planningSlotHeaders(slots: AvailableSlotOption[]): string[] {
  return WEEKDAYS.flatMap((weekday) => {
    const indexes = { AM: 0, PM: 0 };
    return slots.map((slot) => {
      indexes[slot.day_part] += 1;
      const part = slot.day_part === "AM" ? "上午" : "下午";
      return `${weekday}｜${part}第${indexes[slot.day_part]}节（${slot.display_time}）`;
    });
  });
}

async function sheetRows(
  sheet: WorkSheet,
  sheetName: string,
  expectedHeaders: string[],
  compatibleHeaders: string[][] = []
): Promise<Array<{ row: unknown[]; sourceRow: number }>> {
  const XLSX = await import("xlsx");
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: null,
    blankrows: true
  });
  const actualHeaders = (matrix[1] ?? []).map(cellText);
  const accepted = [expectedHeaders, ...compatibleHeaders].some(
    (headers) =>
      actualHeaders.length === headers.length &&
      headers.every((header, index) => actualHeaders[index] === header)
  );
  if (!accepted) {
    throw new PlanningWorkbookError("表头与当前项目模板不一致，请重新下载模板", {
      sheetName,
      sourceRow: 2
    });
  }
  return matrix
    .slice(2)
    .map((row, index) => ({ row, sourceRow: index + 3 }))
    .filter(({ row }) => row.some((value) => cellText(value) !== ""));
}

function legacyPlanningSlotHeaders(headers: string[]): string[] {
  return headers.map((header) => header.replace(/（[^）]*）$/, "（）"));
}

function uniqueLookup(entries: Array<[string, string]>, label: string): Map<string, string> {
  const result = new Map<string, string>();
  for (const [name, id] of entries) {
    if (result.has(name)) {
      throw new PlanningWorkbookError(`当前项目存在同名${label}“${name}”，暂无法生成唯一导入对应`);
    }
    result.set(name, id);
  }
  return result;
}

function lookupValue(
  lookup: Map<string, string>,
  value: unknown,
  sheetName: string,
  sourceRow: number,
  field: string
): string {
  const text = cellText(value);
  const id = lookup.get(text);
  if (!id) {
    throw new PlanningWorkbookError(`“${text || "空值"}”不在当前项目可选范围内`, {
      sheetName,
      sourceRow,
      field
    });
  }
  return id;
}

function integerValue(
  value: unknown,
  minimum: number,
  maximum: number,
  sheetName: string,
  sourceRow: number,
  field: string
): number {
  const parsed = typeof value === "number" ? value : Number(cellText(value));
  if (!Number.isInteger(parsed) || parsed < minimum || parsed > maximum) {
    throw new PlanningWorkbookError(`必须是 ${minimum}～${maximum} 的整数`, {
      sheetName,
      sourceRow,
      field
    });
  }
  return parsed;
}

function booleanValue(
  value: unknown,
  sheetName: string,
  sourceRow: number,
  field: string
): boolean {
  return enumValue(value, { "否": false, "是": true } as const, sheetName, sourceRow, field);
}

function enumValue<T>(
  value: unknown,
  choices: Record<string, T>,
  sheetName: string,
  sourceRow: number,
  field: string
): T {
  const text = cellText(value);
  if (!Object.prototype.hasOwnProperty.call(choices, text)) {
    throw new PlanningWorkbookError(`“${text || "空值"}”不是有效选项`, {
      sheetName,
      sourceRow,
      field
    });
  }
  return choices[text]!;
}

function cellText(value: unknown): string {
  return value === null || value === undefined ? "" : String(value).trim();
}
